import { PopoutableContent } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { hasStackingContextIssues } from "client_legacy/ndaw/mobile";
import { TokenInfo } from "client/Api/Apps/PowerBi/powerBi";
import {
	ReactElement,
	useCallback,
	useContext,
	useMemo,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import {
	decodePowerBiBase64,
	PowerBiEmbed,
	PowerBiMessage
} from "./PowerBiEmbed";
import { PowerBiPopout } from "./PowerBiPopout";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";
import classNames from "classnames";
import styles from "./PowerBiLiveTileContent.module.less";

export type PowerBiLiveTileContentProps = {
	embedUrlBase: string;
	dashboardId: string;
	powerBiTileId: string;
	tokenInfo: TokenInfo | null;
	isConfiguredCorrectly: boolean;
	onNavigate: () => void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const PowerBiLiveTileContent = (props: PowerBiLiveTileContentProps): ReactElement => {
	const {
		embedUrlBase,
		dashboardId,
		powerBiTileId,
		tokenInfo,
		isConfiguredCorrectly,
		onNavigate,
		onOpenTileMenu
	} = props;

	const [isPoppedOut, setIsPoppedOut] = useState<boolean>(false);
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const {
		icon,
		id,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const useSeparateDomForPopout: boolean = useMemo(
		() => hasStackingContextIssues(),
		[]
	);

	const popOutHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			setIsPoppedOut(true);
		},
		[addAnalyticsEvent]
	);

	const popInHandler = useCallback(
		(): void => setIsPoppedOut(false),
		[]
	);

	const errorHandler = useCallback(
		(): void => {
			setLoadingState({
				isLoading: false,
				hasError: true
			});
		},
		[]
	);

	const loadedHandler = useCallback(
		(message: PowerBiMessage): void => {
			let title: string | null = null;
			try {
				const tileData: any = JSON.parse(decodePowerBiBase64(message.tileData.data));
				if (tileData.displayText) {
					title = tileData.displayText;
				} else {
					const config: any = JSON.parse(tileData.config);
					title = config.visual.heading1;
				}
			} catch (e) {
				console.error("Couldn't not get the title", e);
			}

			setLoadingState({
				isLoading: false,
				hasError: false,
				title
			});
		},
		[]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate();
		},
		[addAnalyticsEvent, onNavigate]
	);

	let title: string | null = null;
	let canBeEnlarged: boolean = false;

	if (!loadingState.isLoading && !loadingState.hasError) {
		title = loadingState.title;
		canBeEnlarged = true;
	}

	const defaultActions = useMemo(
		(): readonly Action<ActionContext>[] => listDefaultActions({
			enlarge: () => popOutHandler()
		}),
		[popOutHandler]
	);

	const actionContext = useMemo(
		(): ActionContext => ({
			canBeEnlarged
		}),
		[canBeEnlarged]
	);

	const renderContent = (): ReactElement | null => {
		if (!isConfiguredCorrectly) {
			return (
				<LiveTileContent.Message text={localize("_IncompleteTileConfiguration")} />
			);
		}

		if (!tokenInfo || (!loadingState.isLoading && loadingState.hasError)) {
			return (
				<LiveTileContent.Message text={localize("_CouldNotRetrieveTheTileFromPowerBi")} />
			);
		}

		return (
			<>
				<div
					onClick={canBeEnlarged ? popOutHandler : undefined}
					className={
						classNames(
							styles.contentOverlay,
							{[styles.contentOverlay_clickable]: canBeEnlarged}
						)
					}
				/>

				<PopoutableContent
					isPoppedOut={!useSeparateDomForPopout && isPoppedOut}
					onPopin={popInHandler}
					title={name}
				>
					<PowerBiEmbed
						embedUrlBase={embedUrlBase}
						dashboardId={dashboardId}
						tileId={powerBiTileId}
						accessToken={tokenInfo.token}
						trackingId="embed"
						onError={errorHandler}
						onLoaded={loadedHandler}
					/>
				</PopoutableContent>
			</>
		);
	};

	return (
		<LiveTileContent
			icon={icon}
			name={name}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
			isPoppedOut={isPoppedOut}
		>
			<LiveTileContent.Header
				icon={icon}
				actions={defaultActions}
				actionContext={actionContext}
			>
				<LiveTileContent.Title text={title ?? ""} />
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					{renderContent()}
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>

			{useSeparateDomForPopout && isPoppedOut && tokenInfo && (
				<PowerBiPopout
					tileId={id}
					name={name}
					embedUrlBase={embedUrlBase}
					dashboardId={dashboardId}
					powerBiTileId={powerBiTileId}
					tokenInfo={tokenInfo}
					onClose={popInHandler}
				/>
			)}
		</LiveTileContent>
	);
};
PowerBiLiveTileContent.displayName = "PowerBiLiveTileContent";

type LoadingState = {
	isLoading: true;
} | (
	{
		isLoading: false;
	} & (
		{
			hasError: true;
		}
		|
		{
			hasError: false;
			title: string | null;
		}
	)
);

type ActionContext = {
	canBeEnlarged: boolean;
};

type ActionHandlers = {
	enlarge: () => void;
};

function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<ActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "enlarge",
			title: localize("_Enlarge"),
			icon: "arrow-up-right-from-square",
			canExecute: (x) => x.canBeEnlarged,
			execute: () => handlers.enlarge()
		}
	];
}
